import type { CSSProperties, ReactNode } from 'react';
import { IconAlertCircle, IconCheck, IconInfo } from '../icons';
import { cx } from './cx';
import s from './Feedback.module.css';

export function Skeleton({ width = '100%', height = 16, radius, className }: { width?: CSSProperties['width']; height?: CSSProperties['height']; radius?: CSSProperties['borderRadius']; className?: string }) {
  return <span className={cx(s.skeleton, className)} style={{ width, height, borderRadius: radius }} aria-hidden />;
}

export function EmptyState({ icon, title, children, action }: { icon?: ReactNode; title: ReactNode; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className={s.empty}>
      {icon && <span className={s.emptyIcon}>{icon}</span>}
      <p className={s.emptyTitle}>{title}</p>
      {children && <p className={s.emptyText}>{children}</p>}
      {action && <div className={s.emptyAction}>{action}</div>}
    </div>
  );
}

export function Alert({ tone = 'info', title, children, action }: { tone?: 'info' | 'warning' | 'danger' | 'success'; title?: ReactNode; children?: ReactNode; action?: ReactNode }) {
  const Icon = tone === 'success' ? IconCheck : tone === 'info' ? IconInfo : IconAlertCircle;
  return (
    <div className={cx(s.alert, s[tone])} role={tone === 'danger' ? 'alert' : 'status'}>
      <Icon size={18} />
      <div className={s.alertBody}>
        {title && <span className={s.alertTitle}>{title}</span>}
        {children}
      </div>
      {action}
    </div>
  );
}

/** Progress bar with an optional marker (e.g. the minimum tickets a round needs). */
export function Progress({ value, max, marker, label }: { value: number; max: number; marker?: number; label: string }) {
  const pct = max > 0 ? Math.min(100, (100 * value) / max) : 0;
  return (
    <div className={s.progress} role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={max} aria-valuenow={value}>
      <span className={s.progressBar} style={{ width: `${pct}%` }} />
      {marker !== undefined && marker > 0 && marker < max && <span className={s.progressMarker} style={{ left: `${(100 * marker) / max}%` }} />}
    </div>
  );
}

export function Avatar({ name, size = 32 }: { name: string; size?: number }) {
  const initials = name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => [...p][0]?.toUpperCase() ?? '')
    .join('');
  return (
    <span className={s.avatar} style={{ width: size, height: size, fontSize: size * 0.4 }} aria-hidden>
      {initials || '?'}
    </span>
  );
}
